export type Severity = "operational" | "degraded" | "partial_outage" | "major_outage" | "maintenance" | "unknown";

export const SEVERITY_LABEL: Record<Severity, string> = {
  operational: "Betriebsbereit",
  degraded: "Eingeschränkt",
  partial_outage: "Teilausfall",
  major_outage: "Schwerer Ausfall",
  maintenance: "Wartung",
  unknown: "Unbekannt",
};

export const SEVERITY_COLOR: Record<Severity, string> = {
  operational: "bg-emerald-500",
  degraded: "bg-amber-500",
  partial_outage: "bg-orange-500",
  major_outage: "bg-red-600",
  maintenance: "bg-blue-500",
  unknown: "bg-muted-foreground/40",
};

export const SEVERITY_RANK: Record<Severity, number> = {
  unknown: -1,
  operational: 0,
  maintenance: 1,
  degraded: 2,
  partial_outage: 3,
  major_outage: 4,
};

export const STATUS_LABEL: Record<string, string> = {
  investigating: "Wird untersucht",
  identified: "Ursache identifiziert",
  monitoring: "Wird beobachtet",
  resolved: "Behoben",
};

export function worstSeverity(list: Severity[]): Severity {
  let best: Severity = "operational";
  for (const s of list) {
    if (SEVERITY_RANK[s] > SEVERITY_RANK[best]) best = s;
  }
  return best;
}

export function dayKey(d: Date | string): string {
  const dt = typeof d === "string" ? new Date(d) : d;
  return dt.toISOString().slice(0, 10);
}

export function last31Days(): string[] {
  const days: string[] = [];
  const today = new Date();
  for (let i = 30; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    days.push(dayKey(d));
  }
  return days;
}

/** YYYY-MM-DDTHH (lokal-unabhängig: UTC ISO bis Stunde) */
export function hourKey(d: Date | string): string {
  const dt = typeof d === "string" ? new Date(d) : d;
  return dt.toISOString().slice(0, 13);
}

export interface HourBucket {
  key: string;        // YYYY-MM-DDTHH
  date: Date;         // bucket start (UTC)
  isDayBoundary: boolean;
}

/** Letzte N Tage stündlich (default 14 → 336 Buckets) */
export function lastHours(days = 14): HourBucket[] {
  const buckets: HourBucket[] = [];
  const now = new Date();
  const cur = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), now.getUTCHours()));
  const total = days * 24;
  for (let i = total - 1; i >= 0; i--) {
    const d = new Date(cur.getTime() - i * 3600 * 1000);
    buckets.push({
      key: hourKey(d),
      date: d,
      isDayBoundary: d.getUTCHours() === 0,
    });
  }
  return buckets;
}

/** Uptime in Prozent (operational + maintenance zählen als up; unknown wird ignoriert). */
export function uptimePercent(buckets: HourBucket[], statusByHour: Map<string, Severity>): number {
  let up = 0;
  let known = 0;
  for (const b of buckets) {
    const s = statusByHour.get(b.key) ?? "unknown";
    if (s === "unknown") continue;
    known++;
    if (s === "operational" || s === "maintenance") up++;
  }
  if (known === 0) return 100;
  return Math.round((up / known) * 10000) / 100;
}

/**
 * Erste bekannte Messung pro Kategorie. Stunden vor diesem Zeitpunkt bleiben "unknown"
 * (es gab schlicht noch keine Messdaten); ab diesem Zeitpunkt nehmen wir an, dass
 * der Dienst lief, wenn weder ein Health-Check-Eintrag noch ein Incident existiert.
 */
export function firstSeenAtByCat<T extends { category_id: string; checked_at: string }>(
  health: T[],
): Map<string, Date> {
  const out = new Map<string, Date>();
  for (const h of health) {
    const cur = out.get(h.category_id);
    const d = new Date(h.checked_at);
    if (!cur || d < cur) out.set(h.category_id, d);
  }
  return out;
}